import { useState } from 'react';
import { Alert, Button, notification } from 'antd';
import { DownOutlined, SafetyCertificateOutlined, WarningOutlined } from '@ant-design/icons';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import { MIN_REASON, unwrap } from 'pages/access-groups/common';
import { ActionConfig, ActionModal, useStaff } from './shared';
import { ConfigBody, StaffAccessMode, StaffAccessSummary } from './types';
import styles from './styles.module.scss';

const MODE_TEXT: Record<StaffAccessMode, string> = {
  ENFORCE: 'Đang chặn tự khai (khuyên dùng)',
  LEGACY: 'Đang cho tự khai như cũ',
};

/**
 * Đầu mỗi thẻ: cảnh báo thiếu bảng dữ liệu + khu "chế độ tự khai" (thu gọn sẵn).
 * Đặt trong từng thẻ vì components/tabbed-page không có chỗ chèn giữa tiêu đề và thanh thẻ;
 * trạng thái mở/đóng giữ ở trang nên đổi thẻ không bị đóng lại.
 */
const TabHead = () => {
  const { summary, applySummary, afterAction, settingsOpen, setSettingsOpen } = useStaff();
  const [action, setAction] = useState<ActionConfig | null>(null);
  const enforcing = summary.mode !== 'LEGACY';

  const switchMode = (mode: StaffAccessMode) => {
    const toLegacy = mode === 'LEGACY';
    setAction({
      title: toLegacy ? 'Cho tự khai như cũ?' : 'Bật lại chặn tự khai?',
      okText: toLegacy ? 'Cho tự khai như cũ' : 'Bật chặn tự khai',
      danger: toLegacy,
      description: (
        <p style={{ marginBottom: 12 }}>
          {toLegacy
            ? 'Người dùng sẽ lại tự chọn “nhân viên” và tự đổi phòng ban được ngay, không cần Admin duyệt. Chỉ dùng khi cần gấp; yêu cầu vẫn được ghi lại để đối chiếu sau.'
            : 'Từ lúc bật, ai tự chọn “nhân viên” hoặc tự đổi phòng ban sẽ phải chờ Admin duyệt ở trang này.'}
        </p>
      ),
      noteLabel: `Lý do đổi chế độ (bắt buộc, ít nhất ${MIN_REASON} ký tự)`,
      noteMin: MIN_REASON,
      onSubmit: async (reason) => {
        const body: ConfigBody = { mode, reason };
        const res = await AxiosUtils.put(API.staffAccessConfig, body);
        const next = unwrap<StaffAccessSummary>(res, (data) => typeof data?.mode === 'string');
        if (next) applySummary(next);
        afterAction();
        notification.success({
          message: toLegacy ? 'Đã cho tự khai như cũ.' : 'Đã bật chặn tự khai.',
        });
      },
    });
  };

  return (
    <>
      {!summary.tableReady && (
        <Alert
          className={styles.notice}
          type="warning"
          showIcon
          message="Bảng dữ liệu chưa được tạo trên máy chủ. Việc tự khai vẫn bị chặn nhưng chưa lưu được yêu cầu."
        />
      )}
      <section className={styles.mode} aria-label="Chế độ tự khai nhân viên">
        <button
          type="button"
          className={styles.modeHead}
          aria-expanded={settingsOpen}
          aria-controls="staff-mode-body"
          onClick={() => setSettingsOpen(!settingsOpen)}
        >
          <span
            className={styles.modeState}
            style={{ color: enforcing ? colors.successInk : colors.warningInk }}
          >
            {enforcing ? (
              <SafetyCertificateOutlined aria-hidden />
            ) : (
              <WarningOutlined aria-hidden />
            )}
            {MODE_TEXT[enforcing ? 'ENFORCE' : 'LEGACY']}
          </span>
          <span className={styles.modeToggle}>
            {settingsOpen ? 'Thu gọn' : 'Cài đặt'}
            <DownOutlined
              className={
                settingsOpen
                  ? `${styles.modeChevron} ${styles.modeChevronOpen}`
                  : styles.modeChevron
              }
              aria-hidden
            />
          </span>
        </button>
        {settingsOpen && (
          <div id="staff-mode-body" className={styles.modeBody}>
            <ul className={styles.modeList}>
              <li>
                <strong>Chặn tự khai (khuyên dùng):</strong> ai tự chọn “nhân viên” lúc đăng ký hoặc
                tự đổi phòng ban trong hồ sơ thì vẫn là người dùng thường, và có một yêu cầu nằm ở
                thẻ “Chờ duyệt” cho Admin quyết định.
              </li>
              <li>
                <strong>Cho tự khai như cũ:</strong> người dùng tự chọn là thành nhân viên ngay, như
                trước đây. Chỉ nên bật tạm khi cần gấp.
              </li>
            </ul>
            {enforcing ? (
              <Button danger onClick={() => switchMode('LEGACY')}>
                Chuyển sang cho tự khai như cũ…
              </Button>
            ) : (
              <Button type="primary" onClick={() => switchMode('ENFORCE')}>
                Bật lại chặn tự khai…
              </Button>
            )}
          </div>
        )}
      </section>
      <ActionModal config={action} onClose={() => setAction(null)} />
    </>
  );
};

export default TabHead;
